import { Component } from '@theme/component';

/**
 * @typedef {Object} GiftGuideResult
 * @property {string[]} answers - The answer handles that lead to this result.
 * @property {string} url - The URL of the result page.
 */

/**
 * @typedef {Object} GiftGuideRefs
 * @property {HTMLElement[]} steps
 * @property {HTMLElement} [fallback]
 * @property {HTMLScriptElement} [resultsData]
 */

/** The query parameter holding the answers picked so far. */
const ANSWERS_PARAM = 'a';

/**
 * Drives the gift guide: step navigation, answer selection and the jump to the
 * matching result page.
 *
 * Answers are matched as a set, never by position, so reordering questions or
 * answers in the theme editor cannot silently break the mapping.
 *
 * @extends {Component<GiftGuideRefs>}
 */
class GiftGuideComponent extends Component {
  requiredRefs = ['steps'];

  /** @type {GiftGuideResult[]} */
  #results = [];

  /** How many history entries this component pushed, so `previous` can unwind them. */
  #pushedEntries = 0;

  /** @type {HTMLLinkElement | null} */
  #prefetchLink = null;

  #onPopState = () => {
    this.#pushedEntries = Math.max(0, this.#pushedEntries - 1);
    this.#restoreFromUrl();
  };

  connectedCallback() {
    super.connectedCallback();
    this.#results = this.#parseResults();
    window.addEventListener('popstate', this.#onPopState);
    this.#restoreFromUrl();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this.#onPopState);
  }

  /**
   * The steps holding a question, in document order. The entry screen is excluded.
   * @returns {HTMLElement[]}
   */
  get #questionSteps() {
    return this.refs.steps.filter((step) => step.dataset.step !== '0');
  }

  /**
   * The step currently shown, or the entry screen when nothing is shown yet.
   * @returns {HTMLElement | undefined}
   */
  get #currentStep() {
    return this.refs.steps.find((step) => !step.hidden) ?? this.refs.steps[0];
  }

  /**
   * The answer handles picked so far, in question order.
   * @returns {string[]}
   */
  get #answers() {
    return this.#questionSteps
      .map((step) => step.querySelector('input[type="radio"]:checked'))
      .filter((input) => input instanceof HTMLInputElement)
      .map((input) => input.value);
  }

  /** Moves forward: entry to first question, question to question, last question to the result. */
  next() {
    const current = this.#currentStep;
    if (!current) return;

    const index = Number(current.dataset.step);
    if (Number.isNaN(index)) return;

    if (index > 0 && !current.querySelector('input[type="radio"]:checked')) return;

    if (index === 0) {
      this.#track('gift_guide_start', { gg_total_steps: this.#questionSteps.length });
    }

    if (index >= this.#questionSteps.length) {
      this.#goToResult();
      return;
    }

    this.#showStep(index + 1);
    this.#pushUrl();
  }

  /** Moves back one step, unwinding our own history entries so the back button stays honest. */
  previous() {
    if (this.#pushedEntries > 0) {
      history.back();
      return;
    }

    const current = this.#currentStep;
    if (!current) return;

    const index = Number(current.dataset.step);
    if (Number.isNaN(index) || index === 0) return;

    this.#clearAnswersFrom(index);
    this.#showStep(index - 1);
    this.#replaceUrl();
  }

  /** Clears every answer and returns to the entry screen. */
  restart() {
    this.#track('gift_guide_restart', { gg_answers: this.#answers.join(',') });

    for (const input of this.querySelectorAll('input[type="radio"]')) {
      if (input instanceof HTMLInputElement) input.checked = false;
    }

    this.#showStep(0);
    history.replaceState({ giftGuide: true }, '', this.#buildUrl());
    this.#pushedEntries = 0;
  }

  /**
   * Enables the step's call to action once an answer is picked, and warms up the
   * result page as soon as the last answer is known.
   * @param {Event} event
   */
  handleAnswerChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    const step = target.closest('[data-step]');
    if (!(step instanceof HTMLElement)) return;

    const cta = step.querySelector('.gift-guide__cta');
    if (cta instanceof HTMLButtonElement) cta.disabled = false;

    this.#track('gift_guide_answer', {
      gg_step: Number(step.dataset.step),
      gg_question: this.#questionOf(step),
      gg_answer: target.value,
      gg_answer_label: step.querySelector(`label[for="${target.id}"]`)?.textContent?.trim() ?? '',
    });

    if (Number(step.dataset.step) === this.#questionSteps.length) {
      this.#prefetchResult();
    }
  }

  /**
   * Pushes an event to the GTM data layer. The layer is created by the Analyzify
   * extension, so it may not exist yet when the guide starts.
   * @param {string} event
   * @param {Record<string, unknown>} [payload]
   */
  #track(event, payload = {}) {
    const dataLayer = (window.dataLayer ??= []);
    dataLayer.push({ event, ...payload });
  }

  /**
   * The question asked by a step, used to read the funnel without a lookup table.
   * @param {HTMLElement | undefined} step
   * @returns {string}
   */
  #questionOf(step) {
    return step?.querySelector('.gift-guide__question')?.textContent?.trim() ?? '';
  }

  /**
   * Reads the results table rendered by the section.
   * @returns {GiftGuideResult[]}
   */
  #parseResults() {
    const { resultsData } = this.refs;
    if (!resultsData?.textContent) return [];

    try {
      const parsed = JSON.parse(resultsData.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('Gift guide: could not read the results table', error);
      return [];
    }
  }

  /**
   * Finds the result matching a set of answers, ignoring their order.
   * @param {string[]} answers
   * @returns {GiftGuideResult | undefined}
   */
  #findResult(answers) {
    if (answers.length === 0) return undefined;

    const key = [...answers].sort().join('|');
    return this.#results.find((result) => [...result.answers].sort().join('|') === key);
  }

  /** Sends the visitor to the result page, or to the fallback screen when nothing matches. */
  #goToResult() {
    const result = this.#findResult(this.#answers);

    if (!result) {
      this.#showFallback();
      return;
    }

    window.location.assign(result.url);
  }

  /** Warms the browser cache for the result the visitor is about to reach. */
  #prefetchResult() {
    const result = this.#findResult(this.#answers);
    if (!result) return;

    if (!this.#prefetchLink) {
      this.#prefetchLink = document.createElement('link');
      this.#prefetchLink.rel = 'prefetch';
      document.head.append(this.#prefetchLink);
    }

    this.#prefetchLink.href = result.url;
  }

  /**
   * Shows a single step and hides every other one.
   * @param {number} index
   */
  #showStep(index) {
    const { fallback, steps } = this.refs;

    for (const step of steps) {
      step.hidden = Number(step.dataset.step) !== index;
    }

    if (fallback) fallback.hidden = true;

    const current = steps.find((step) => Number(step.dataset.step) === index);
    const cta = current?.querySelector('.gift-guide__cta');

    if (cta instanceof HTMLButtonElement && index > 0) {
      cta.disabled = !current?.querySelector('input[type="radio"]:checked');
    }

    if (index > 0) {
      this.#track('gift_guide_step', {
        gg_step: index,
        gg_total_steps: this.#questionSteps.length,
        gg_question: this.#questionOf(current),
      });
    }
  }

  /** Shows the editorial fallback screen used when no result covers the combination. */
  #showFallback() {
    const { fallback, steps } = this.refs;
    this.#track('gift_guide_no_result', { gg_answers: this.#answers.join(',') });
    if (!fallback) return;

    for (const step of steps) step.hidden = true;
    fallback.hidden = false;
  }

  /**
   * Unchecks every answer from the given step onwards.
   * @param {number} index
   */
  #clearAnswersFrom(index) {
    for (const step of this.#questionSteps) {
      if (Number(step.dataset.step) < index) continue;

      for (const input of step.querySelectorAll('input[type="radio"]')) {
        if (input instanceof HTMLInputElement) input.checked = false;
      }
    }
  }

  /**
   * Builds the URL carrying the answers picked so far.
   * @returns {string}
   */
  #buildUrl() {
    const url = new URL(window.location.href);
    const answers = this.#answers;

    if (answers.length === 0) {
      url.searchParams.delete(ANSWERS_PARAM);
    } else {
      url.searchParams.set(ANSWERS_PARAM, answers.join(','));
    }

    return url.toString();
  }

  #pushUrl() {
    history.pushState({ giftGuide: true }, '', this.#buildUrl());
    this.#pushedEntries += 1;
  }

  #replaceUrl() {
    history.replaceState({ giftGuide: true }, '', this.#buildUrl());
    this.#pushedEntries = Math.max(0, this.#pushedEntries - 1);
  }

  /** Restores the step and the answers described by the current URL. */
  #restoreFromUrl() {
    const param = new URL(window.location.href).searchParams.get(ANSWERS_PARAM);
    const answers = param ? param.split(',').filter(Boolean) : [];
    const questionSteps = this.#questionSteps;

    for (const [index, step] of questionSteps.entries()) {
      const handle = answers[index];
      for (const input of step.querySelectorAll('input[type="radio"]')) {
        if (input instanceof HTMLInputElement) input.checked = Boolean(handle) && input.value === handle;
      }
    }

    if (answers.length >= questionSteps.length && questionSteps.length > 0) {
      this.#goToResult();
      return;
    }

    this.#showStep(Math.min(answers.length + (answers.length > 0 ? 1 : 0), questionSteps.length));
  }
}

if (!customElements.get('gift-guide')) {
  customElements.define('gift-guide', GiftGuideComponent);
}
